import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  RECOVERY_REQUESTS_URL,
  usePendingRecoveryRequests,
} from 'contexts/ui/usePendingRecoveryRequests';

import { RecoveryRequestsMessage } from './RecoveryRequestsMessage';

export const RECOVERY_REQUESTS_NOTIFICATION_ID = 'recovery-requests';

const hideNotice = (): void => {
  notifications.hide(RECOVERY_REQUESTS_NOTIFICATION_ID);
};

const reviewRequests = (): void => {
  hideNotice();
  window.location.href = RECOVERY_REQUESTS_URL;
};

/**
 * Tells a Compete 2.0 admin that account-recovery requests wait for their approval, with a
 * button that opens the v1 page where they approve or deny them. The notice stays until the
 * admin closes it or no requests are left. If the admin closes it, it comes back only when the
 * number of pending requests changes.
 *
 * Compete 2.0 pages have no AppUIProvider, so this is the Mantine counterpart of the
 * RecoveryRequestsNotifier in contexts/ui.
 */
export function RecoveryRequestsNotifier(): null {
  const { t } = useTranslation(['Session']);
  const pendingRecoveryCount = usePendingRecoveryRequests();

  useEffect(() => {
    if (pendingRecoveryCount === 0) {
      hideNotice();
      return;
    }

    const notification = {
      id: RECOVERY_REQUESTS_NOTIFICATION_ID,
      title: t('Session:recoveryRequests.title'),
      message: (
        <RecoveryRequestsMessage
          count={pendingRecoveryCount}
          onReview={reviewRequests}
        />
      ),
      autoClose: false as const,
      withCloseButton: true,
    };

    // Mantine ignores a show() whose id is already on screen, so show() only adds the notice
    // when it is missing, and update() then sets the new count.
    notifications.show(notification);
    notifications.update(notification);
  }, [pendingRecoveryCount, t]);

  useEffect(() => hideNotice, []);

  return null;
}
